import React from 'react';
import QRCode from 'react-qr-code';

import { Button, Heading, Modal, Stack, Text } from '@auspost/postmaster-react';

import MobileDeviceIcon from 'assets/mobile-device.svg';
import TrustedNotificationsIcon from 'assets/trusted-notifications.svg';
import { config } from 'config/env-config';
import { trackInteraction } from 'store/analytics/analytics.thunks';
import { useAppDispatch } from 'store/store';

import useMediaQuery from '../../DialogueCardPanel/hooks/MediaQuery.hooks';
import { DOWNLOAD_APP_MODAL_BUTTON_TEST_ID, DOWNLOAD_APP_MODAL_QRCODE_TEST_ID } from './DownloadAppModal.test-ids';

import styles from './DownloadAppModal.module.scss';

interface DownloadAppModalProp {
  handleClose: () => void;
  isOpen: boolean;
  branchIoLink: string;
  branchDeepLink: string;
  modalContent: string;
}

const DownloadAppModal: React.FC<DownloadAppModalProp> = ({
  handleClose,
  isOpen,
  branchIoLink,
  branchDeepLink,
  modalContent,
}) => {
  const dispatch = useAppDispatch();
  const matches = useMediaQuery('(max-width: 480px)');

  const branchLink = `${config.env.branchIoUrl}/${branchIoLink}?$deeplink_path=${branchDeepLink}`;

  const handleOnClick = () => {
    dispatch(trackInteraction('download mobile app'));
    window.location.assign(branchLink);
  };

  const handleOnClose = () => {
    dispatch(trackInteraction('closed download app modal'));
    handleClose();
  };

  return (
    <Modal
      isModalShowing={isOpen}
      onHide={handleOnClose}
      modalClassName={styles.downloadAppModal}
      dialogClassName={styles.downloadAppDialog}
      ariaDialogLabel='download app modal'
      dialogId='download app modal'
    >
      {!matches && <Modal.Header className={styles.header} />}
      <Modal.Content className={styles.modalContent} onKeyDown={(e) => e.key === 'Escape' && handleOnClose()}>
        <Stack space='medium' className={styles.innerContent}>
          <TrustedNotificationsIcon />
          <div className={styles.content}>
            <Heading level='h2' className={styles.heading}>
              Available on the AusPost app
            </Heading>
            <Text variant={matches ? 'body' : 'body-small'} className={styles.description}>
              {modalContent}
            </Text>
          </div>
          {!matches ? (
            <div>
              <div className={styles.qrCodeButton}>
                <MobileDeviceIcon />
                <div className={styles.qrCodeTextContainer}>
                  <Text variant='body-small' weight='bold' className={styles.qrCodeText}>
                    Scan to continue in the app
                  </Text>
                </div>
                <div className={styles.qrCode}>
                  <QRCode
                    value={branchLink}
                    size={70}
                    aria-label='Download app QR code'
                    data-testid={DOWNLOAD_APP_MODAL_QRCODE_TEST_ID}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.buttonContainer}>
              <Button
                onClick={handleOnClick}
                size='medium'
                variant='primary'
                data-testid={DOWNLOAD_APP_MODAL_BUTTON_TEST_ID}
                className={styles.btn}
              >
                Continue in the app
              </Button>
              <Button onClick={handleOnClose} size='medium' variant='tertiary' className={styles.btn}>
                Maybe later
              </Button>
            </div>
          )}
        </Stack>
      </Modal.Content>
    </Modal>
  );
};

export default DownloadAppModal;
